import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api, errorText } from '../api/client'
import type { Subject, Task } from '../api/types'
import { dateInput, dueLabel } from '../lib/due'
import { subjectKey } from '../lib/subject'
import { useAsync } from '../lib/useAsync'
import { DateField } from './DateField'
import { Icon } from './Icon'
import { ErrorState, Loading } from './States'
import { useToast } from './Toast'

const TONE_COLOR = { danger: 'var(--danger)', warning: 'var(--warning)', neutral: 'var(--ink-3)',
  positive: 'var(--positive)', accent: 'var(--accent)' } as const

/** Tasks for one subject, or the signed-in officer's own list when no subject is
 *  given. Completing a task offers Undo. */
export function TaskList({
  subject,
  scope = 'mine',
  status = 'open',
  allowAdd = true,
  showRegarding = false,
  refreshKey = 0,
  onChange,
  emptyText = 'No open tasks.',
}: {
  subject?: Subject
  scope?: 'mine' | 'all'
  status?: 'open' | 'done' | 'all'
  allowAdd?: boolean
  showRegarding?: boolean
  refreshKey?: number
  onChange?: () => void
  emptyText?: string
}) {
  const { data, error, loading, reload } = useAsync(
    () => api.tasks({ scope, status, subject }),
    [subject ? subjectKey(subject) : 'none', scope, status, refreshKey],
  )
  const [title, setTitle] = useState('')
  const [due, setDue] = useState(dateInput(0))
  const [busy, setBusy] = useState(false)
  const toast = useToast()

  const changed = () => {
    reload()
    onChange?.()
  }

  async function toggle(task: Task) {
    const done = !task.done_at
    try {
      await api.updateTask(task.id, { done })
      changed()
      if (done) {
        toast(`Done: ${task.title}`, {
          tone: 'positive',
          action: {
            label: 'Undo',
            onClick: () => api.updateTask(task.id, { done: false }).then(changed),
          },
        })
      }
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    }
  }

  async function add(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) return
    setBusy(true)
    try {
      await api.createTask({ title: title.trim(), due: due || undefined, subject })
      setTitle('')
      changed()
    } catch (err) {
      toast(errorText(err), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  const tasks = data?.tasks ?? []

  return (
    <div>
      {loading && !data ? (
        <Loading label="Loading tasks…" />
      ) : error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : tasks.length === 0 ? (
        <p className="quiet" style={{ margin: '4px 0' }}>{emptyText}</p>
      ) : (
        tasks.map((t) => {
          const d = dueLabel(t.due_at, Boolean(t.done_at))
          return (
            <div className="taskrow" key={t.id}>
              <button
                className={`taskcheck${t.done_at ? ' done' : ''}`}
                onClick={() => toggle(t)}
                aria-label={t.done_at ? `Reopen ${t.title}` : `Complete ${t.title}`}
              >
                <Icon name={t.done_at ? 'check_circle' : 'radio_button_unchecked'} size={20} />
              </button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className={`task-title${t.done_at ? ' done' : ''}`}>{t.title}</div>
                <div className="task-meta">
                  {showRegarding && t.regarding ? (
                    t.href ? <Link to={t.href}>{t.regarding}</Link> : <span>{t.regarding}</span>
                  ) : null}
                  {t.source === 'playbook' ? <span>from a workflow</span> : null}
                  {t.assignee && scope === 'all' ? <span>{t.assignee}</span> : null}
                  {t.notes ? <span title={t.notes}>has notes</span> : null}
                </div>
              </div>
              <span className="task-due" style={{ color: TONE_COLOR[d.tone] }}>{d.text}</span>
            </div>
          )
        })
      )}

      {allowAdd ? (
        <form className="taskadd" onSubmit={add}>
          <input
            className="field"
            placeholder="Add a task…"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            aria-label="New task"
          />
          <DateField value={due} onChange={setDue} label="Due date" clearable style={{ width: 185 }} />
          <button className="btn" disabled={!title.trim() || busy}>Add</button>
        </form>
      ) : null}
    </div>
  )
}
